import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21, default as _styled22, default as _styled23, default as _styled24, default as _styled25, default as _styled26, default as _styled27, default as _styled28, default as _styled29, default as _styled30, default as _styled31, default as _styled32, default as _styled33, default as _styled34, default as _styled35, default as _styled36 } from "styled-components";
import React from "react";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { Icon } from "~/features/ui/icon";
import { Wrap } from "~/features/ui/wrap";
import { Tag } from "~/features/ui/tag";
import { Button } from "~/features/ui/button";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { RichText } from "~/features/ui/rich-text";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { VideoControls } from "~/features/ui/video/video-controls";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { useMediaQuery } from "~/features/ui/use-media-query";
import CaretDown from "~/public/icons/caret-down.svg";
import { MediaFilter } from "./media-filter";
export const productShowcaseFragment = graphql(`
  fragment ProductShowcaseHero on hero_productHeroMedia_BlockType {
    title: baseHeadline
    subtitle: baseText
    description: baseRichText
    link: baseLink {
      ...Link
    }
    specs: baseTable {
      label
      value
    }
    button: baseLink2 {
      ...Link
    }
    supportLink: baseLink3 {
      ...Link
    }
    accordions: baseTable2 {
      label
      value
    }
    mediaItems: children {
      ... on hero_productHeroMediaItem_BlockType {
        media: baseMedia {
          ...Media
        }
      }
    }
  }
`);
function getMediaCaption(media: readonly unknown[] | null | undefined): string | null {
  if (!media || media.length === 0) return null;
  const first = media[0] as Record<string, unknown> | null;
  return first?.caption as string ?? null;
}
function isInlineVideo(media: readonly unknown[] | null | undefined): boolean {
  if (!media || media.length === 0) return false;
  const first = media[0] as Record<string, unknown> | null;
  if (!first) return false;
  return Boolean(first.videoUrl) || Array.isArray(first.videoAsset) && (first.videoAsset as unknown[]).length > 0;
}
type Props = FragmentType<typeof productShowcaseFragment> & {
  tag?: string;
  isFree?: boolean;
};
export function ProductShowcase(props: Props) {
  const {
    tag,
    isFree,
    ...rest
  } = props;
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const mobileTopPadding = isAnnouncementBarVisible ? {
    "paddingTop": "calc(var(--header-height) + var(--announcement-bar-height) + var(--spacing-xl))"
  } : {
    "paddingTop": "calc(var(--header-height) + var(--spacing-xl))"
  };
  const stickyTop = isAnnouncementBarVisible ? {
    "@media (min-width: 1024px)": {
      "top": "calc(var(--header-height) + var(--announcement-bar-height))"
    }
  } : {
    "@media (min-width: 1024px)": {
      "top": "var(--header-height)"
    }
  };
  const mediaHeight = isAnnouncementBarVisible ? {
    "@media (min-width: 1024px)": {
      "height": "calc(100svh - var(--header-height) - var(--announcement-bar-height) - (var(--spacing-xl) * 2))"
    }
  } : {
    "@media (min-width: 1024px)": {
      "height": "calc(100svh - var(--header-height) - (var(--spacing-xl) * 2))"
    }
  };
  // Clears the fixed header at rest (page top). The sticky `top` handles the
  // pinned position; margin sets the flow position and the two don't stack,
  // so the media never tucks behind the header.
  const mediaTopOffset = isAnnouncementBarVisible ? {
    "@media (min-width: 1024px)": {
      "marginTop": "calc(var(--header-height) + var(--announcement-bar-height))"
    }
  } : {
    "@media (min-width: 1024px)": {
      "marginTop": "var(--header-height)"
    }
  };
  const desktopTopPadding = isAnnouncementBarVisible ? {
    "@media (min-width: 1024px)": {
      "paddingTop": "calc(var(--header-height) + var(--announcement-bar-height) + var(--spacing-xl))"
    }
  } : {
    "@media (min-width: 1024px)": {
      "paddingTop": "calc(var(--header-height) + var(--spacing-xl))"
    }
  };
  const {
    title,
    subtitle,
    description,
    link: _link,
    specs,
    button: _button,
    supportLink: _supportLink,
    accordions,
    mediaItems
  } = useFragment(productShowcaseFragment, rest);
  const link = takeFirst(_link);
  const button = takeFirst(_button);
  const supportLink = takeFirst(_supportLink);
  const linkIcon = useLinkIcon(link ?? undefined);
  const buttonIcon = useLinkIcon(button ?? undefined);
  const supportLinkIcon = useLinkIcon(supportLink ?? undefined);
  const [openAccordion, setOpenAccordion] = React.useState<string>("");
  const [activeMediaIdx, setActiveMediaIdx] = React.useState(0);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const validMediaItems = React.useMemo(() => (mediaItems ?? []).filter((item): item is NonNullable<typeof item> & {
    media: NonNullable<NonNullable<typeof item>["media"]>;
  } => item != null && "media" in item && hasItems(item.media)), [mediaItems]);
  const mediaCaptions = React.useMemo(() => validMediaItems.map(item => getMediaCaption(item.media as unknown as readonly unknown[])), [validMediaItems]);
  const hasMultipleMedia = validMediaItems.length > 1;
  const filterOptions = React.useMemo(() => {
    if (!hasMultipleMedia) return [];
    return mediaCaptions.map((caption, idx) => caption ? {
      label: caption,
      value: String(idx)
    } : null).filter((opt): opt is NonNullable<typeof opt> => opt !== null);
  }, [hasMultipleMedia, mediaCaptions]);
  const handleFilterChange = (value: string) => {
    setActiveMediaIdx(Number(value));
  };
  const titleBlock = <>
      {(tag || isFree) && <_StyledDiv>
          {tag && <Tag variant="solid-blue">{tag}</Tag>}
          {isFree && <Tag variant="solid-blue">Free</Tag>}
        </_StyledDiv>}

      {title && <_StyledH>
          {title}
          {subtitle && <_StyledSpan> {subtitle}</_StyledSpan>}
        </_StyledH>}

      {description && <_StyledRichText text={description} />}

      {isLinkValid(link) && <_StyledDiv2>
          <Button as={Link} {...link} variant="underline" icon={linkIcon} />
        </_StyledDiv2>}
    </>;
  return <_StyledSection data-theme="light">
      <_StyledWrap>
        <_StyledDiv3>
          {/* Mobile: title + subtitle shown above */}
          <_StyledDiv4 $_css={[{
          "display": "flex",
          "flexDirection": "column",
          "gap": "var(--spacing-md)",
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingBottom": "var(--spacing-xl)",
          "@media (min-width: 1024px)": {
            "display": "none"
          }
        }, mobileTopPadding]}>{titleBlock}</_StyledDiv4>

          {/* Left: Sticky media */}
          <_StyledDiv5 $_css2={[{
          "width": "100%",
          "@media (min-width: 1024px)": {
            "position": "sticky",
            "width": "70%",
            "alignSelf": "flex-start",
            "padding": "var(--spacing-xl)"
          }
        }, stickyTop, mediaTopOffset]}>
            <_StyledDiv6>
              {validMediaItems.length > 0 ? <_StyledDiv7 $_css3={[{
              "position": "relative",
              "aspectRatio": "16 / 9",
              "overflow": "hidden",
              "@media (min-width: 1024px)": {
                "aspectRatio": "auto",
                "borderRadius": "4px"
              }
            }, mediaHeight]}>
                  {validMediaItems.map((item, idx) => {
                const itemIsVideo = isInlineVideo(item.media as unknown as readonly unknown[]);
                const isActive = idx === activeMediaIdx;
                return <_StyledDiv8 key={idx} style={{
                  opacity: isActive ? 1 : 0,
                  transition: prefersReducedMotion ? "none" : "opacity 700ms cubic-bezier(0.4, 0, 0.2, 1)"
                }} $_css4={[{
                  "height": "100%",
                  "width": "100%"
                }, idx === 0 ? {
                  "position": "relative"
                } : {
                  "position": "absolute",
                  "inset": "0"
                }]}>
                        <Media {...takeFirst(item.media)} imageProps={{
                    fit: "cover",
                    sizes: "lg:65vw",
                    loading: idx === 0 ? "eager" : "lazy",
                    // The first media is the hero's LCP element; load it
                    // eagerly instead of waiting for lazy-load triggers.
                    priority: idx === 0
                  }} externalVideoProps={{
                    fit: "cover"
                  }} inlineVideoProps={itemIsVideo ? {
                    fit: "cover",
                    priority: idx === 0,
                    videoRef: isActive ? videoRef : undefined,
                    controls: isActive ? <_StyledVideoControls videoRef={videoRef} /> : undefined
                  } : undefined} />
                      </_StyledDiv8>;
              })}
                </_StyledDiv7> : <_StyledDiv9>
                  <_StyledSpan2>Product Image</_StyledSpan2>
                </_StyledDiv9>}

              {hasMultipleMedia && hasItems(filterOptions) && <>
                  <_StyledDiv10 data-theme="dark">
                    <MediaFilter options={filterOptions} value={String(activeMediaIdx)} onChange={handleFilterChange} />
                  </_StyledDiv10>
                  <_StyledDiv11 data-theme="dark">
                    <MediaFilter options={filterOptions} value={String(activeMediaIdx)} onChange={handleFilterChange} />
                  </_StyledDiv11>
                </>}
            </_StyledDiv6>
          </_StyledDiv5>

          {/* Right: Scrollable content */}
          <_StyledDiv12 $_css5={[{
          "display": "flex",
          "width": "100%",
          "flexDirection": "column",
          "padding": "var(--spacing-md)",
          "@media (min-width: 1024px)": {
            "width": "30%",
            "padding": "var(--spacing-xl)",
            "paddingLeft": "0"
          }
        }, desktopTopPadding]}>
            <_StyledDiv13>{titleBlock}</_StyledDiv13>

            {hasItems(specs) && <_StyledDiv14>
                {specs.map((spec, idx) => <_StyledDiv15 key={idx} $_css6={[{
              "display": "flex",
              "alignItems": "center",
              "justifyContent": "space-between",
              "borderBottomWidth": "1px",
              "borderColor": "var(--stroke-primary)",
              "paddingTop": "var(--spacing-md)",
              "paddingBottom": "var(--spacing-md)",
              "@media (min-width: 1024px)": {
                "paddingTop": "var(--spacing-md)",
                "paddingBottom": "var(--spacing-md)"
              }
            }]}>
                    {spec.label && <_StyledSpan3>{spec.label}</_StyledSpan3>}
                    {spec.value && <_StyledSpan4>
                        {spec.value}
                      </_StyledSpan4>}
                  </_StyledDiv15>)}
              </_StyledDiv14>}

            <_StyledDiv16>
              {isLinkValid(button) && <Button as={Link} {...button} variant="secondary" icon={buttonIcon} fullWidth />}
              {isLinkValid(supportLink) && <_StyledDiv17>
                  <Button as={Link} {...supportLink} variant="underline" icon={supportLinkIcon} />
                </_StyledDiv17>}
            </_StyledDiv16>

            {hasItems(accordions) && <_StyledDiv18>
                <_StyledH2>
                  Others
                </_StyledH2>

                <RadixAccordion.Root type="single" collapsible value={openAccordion} onValueChange={setOpenAccordion}>
                  {accordions.map((item, idx) => {
                const itemId = item.label || `accordion-${idx}`;
                return <_StyledRadixAccordionItem key={itemId} value={itemId}>
                        <RadixAccordion.Header>
                          <_StyledRadixAccordionTrigger aria-label={item.label || undefined}>
                            <_StyledSpan5>{item.label}</_StyledSpan5>
                            <_StyledIcon icon={CaretDown} size="16" $_css7={[{
                        "transitionProperty": "transform",
                        "transitionDuration": "250ms",
                        "transitionTimingFunction": "ease-in-out"
                      }, openAccordion === itemId && {
                        "--tw-rotate": "180deg",
                        "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
                      }]} />
                          </_StyledRadixAccordionTrigger>
                        </RadixAccordion.Header>
                        <_StyledRadixAccordionContent $_css8={[{
                    "overflow": "hidden"
                  }, {
                    "@keyframes radix-accordion-expand-keyframes": {
                      "from": {
                        "height": "0"
                      },
                      "to": {
                        "height": "var(--radix-accordion-content-height)"
                      }
                    },
                    "&[data-state=\"open\"]": {
                      "animation": "radix-accordion-expand-keyframes 250ms ease-in-out"
                    }
                  }, {
                    "@keyframes radix-accordion-collapse-keyframes": {
                      "from": {
                        "height": "var(--radix-accordion-content-height)"
                      },
                      "to": {
                        "height": "0"
                      }
                    },
                    "&[data-state=\"closed\"]": {
                      "animation": "radix-accordion-collapse-keyframes 250ms ease-in-out"
                    }
                  }]}>
                          <_StyledDiv19>
                            {item.value ? <_StyledP>{item.value}</_StyledP> : <_StyledP2>Content coming soon.</_StyledP2>}
                          </_StyledDiv19>
                        </_StyledRadixAccordionContent>
                      </_StyledRadixAccordionItem>;
              })}
                </RadixAccordion.Root>
              </_StyledDiv18>}
          </_StyledDiv12>
        </_StyledDiv3>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-1vv22lh-0"
})({
  "display": "flex",
  "gap": "var(--spacing-sm)"
});
var _StyledH = _styled2("h1").withConfig({
  componentId: "sc-sf-1vv22lh-1"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = _styled3("span").withConfig({
  componentId: "sc-sf-1vv22lh-2"
})({
  "display": "block",
  "color": "var(--text-transparent)"
});
var _StyledRichText = _styled4(RichText).withConfig({
  componentId: "sc-sf-1vv22lh-3"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-1vv22lh-4"
})({
  "marginTop": "var(--spacing-lg)"
});
var _StyledSection = _styled6("section").withConfig({
  componentId: "sc-sf-1vv22lh-5"
})({
  "position": "relative",
  "backgroundColor": "var(--bg-tertiary)",
  "color": "var(--text-primary)"
});
var _StyledWrap = _styled7(Wrap).withConfig({
  componentId: "sc-sf-1vv22lh-6"
})({
  "borderStyle": "none"
});
var _StyledDiv3 = _styled8("div").withConfig({
  componentId: "sc-sf-1vv22lh-7"
})({
  "display": "flex",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "flexDirection": "row"
  }
});
var _StyledDiv4 = _styled9("div").withConfig({
  componentId: "sc-sf-1vv22lh-8"
})`${p => p.$_css}`;
var _StyledDiv5 = _styled10("div").withConfig({
  componentId: "sc-sf-1vv22lh-9"
})`${p => p.$_css2}`;
var _StyledDiv6 = _styled11("div").withConfig({
  componentId: "sc-sf-1vv22lh-10"
})({
  "position": "relative",
  "padding": "0"
});
var _StyledDiv7 = _styled12("div").withConfig({
  componentId: "sc-sf-1vv22lh-11"
})`${p => p.$_css3}`;
var _StyledDiv8 = _styled13("div").withConfig({
  componentId: "sc-sf-1vv22lh-12"
})`${p => p.$_css4}`;
var _StyledVideoControls = _styled14(VideoControls).withConfig({
  componentId: "sc-sf-1vv22lh-13"
})({
  "position": "absolute",
  "right": "var(--spacing-md)",
  "top": "var(--spacing-md)",
  "zIndex": "10"
});
var _StyledDiv9 = _styled15("div").withConfig({
  componentId: "sc-sf-1vv22lh-14"
})({
  "display": "flex",
  "aspectRatio": "16 / 9",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "center",
  "backgroundColor": "var(--bg-transparent-10)",
  "@media (min-width: 1024px)": {
    "borderRadius": "4px"
  }
});
var _StyledSpan2 = _styled16("span").withConfig({
  componentId: "sc-sf-1vv22lh-15"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "color": "var(--text-transparent)"
});
var _StyledDiv10 = _styled17("div").withConfig({
  componentId: "sc-sf-1vv22lh-16"
})({
  "marginTop": "-0.1rem",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv11 = _styled18("div").withConfig({
  componentId: "sc-sf-1vv22lh-17"
})({
  "position": "absolute",
  "left": "var(--spacing-md)",
  "top": "var(--spacing-md)",
  "zIndex": "10",
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv12 = _styled19("div").withConfig({
  componentId: "sc-sf-1vv22lh-18"
})`${p => p.$_css5}`;
var _StyledDiv13 = _styled20("div").withConfig({
  componentId: "sc-sf-1vv22lh-19"
})({
  "display": "none",
  "flexDirection": "column",
  "gap": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});
var _StyledDiv14 = _styled21("div").withConfig({
  componentId: "sc-sf-1vv22lh-20"
})({
  "marginBottom": "var(--spacing-xl)",
  "marginTop": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "marginTop": "var(--spacing-4xl)"
  }
});
var _StyledDiv15 = _styled22("div").withConfig({
  componentId: "sc-sf-1vv22lh-21"
})`${p => p.$_css6}`;
var _StyledSpan3 = _styled23("span").withConfig({
  componentId: "sc-sf-1vv22lh-22"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledSpan4 = _styled24("span").withConfig({
  componentId: "sc-sf-1vv22lh-23"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv16 = _styled25("div").withConfig({
  componentId: "sc-sf-1vv22lh-24"
})({
  "marginTop": "var(--spacing-md)",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-lg)"
});
var _StyledDiv17 = _styled26("div").withConfig({
  componentId: "sc-sf-1vv22lh-25"
})({
  "display": "flex",
  "justifyContent": "center"
});
var _StyledDiv18 = _styled27("div").withConfig({
  componentId: "sc-sf-1vv22lh-26"
})({
  "paddingTop": "var(--spacing-2xl)"
});
var _StyledH2 = _styled28("h3").withConfig({
  componentId: "sc-sf-1vv22lh-27"
})({
  "borderBottomWidth": "1px",
  "borderColor": "var(--stroke-primary)",
  "paddingTop": "var(--spacing-md)",
  "paddingBottom": "var(--spacing-md)",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "500"
  }
});
var _StyledRadixAccordionItem = _styled29(RadixAccordion.Item).withConfig({
  componentId: "sc-sf-1vv22lh-28"
})({
  "borderBottomWidth": "1px",
  "borderColor": "var(--stroke-primary)"
});
var _StyledRadixAccordionTrigger = _styled30(RadixAccordion.Trigger).withConfig({
  componentId: "sc-sf-1vv22lh-29"
})({
  "display": "flex",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "paddingTop": "var(--spacing-md)",
  "paddingBottom": "var(--spacing-md)",
  "textAlign": "left"
});
var _StyledSpan5 = _styled31("span").withConfig({
  componentId: "sc-sf-1vv22lh-30"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledIcon = _styled32(Icon).withConfig({
  componentId: "sc-sf-1vv22lh-31"
})`${p => p.$_css7}`;
var _StyledRadixAccordionContent = _styled33(RadixAccordion.Content).withConfig({
  componentId: "sc-sf-1vv22lh-32"
})`${p => p.$_css8}`;
var _StyledDiv19 = _styled34("div").withConfig({
  componentId: "sc-sf-1vv22lh-33"
})({
  "paddingBottom": "var(--spacing-lg)"
});
var _StyledP = _styled35("p").withConfig({
  componentId: "sc-sf-1vv22lh-34"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400"
});
var _StyledP2 = /*#__PURE__*/_styled36("p").withConfig({
  displayName: "product-showcase___StyledP2",
  componentId: "sc-12ieait-0"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "color": "var(--text-transparent)"
});